import {
  count,
  get,
  index,
  post,
  put,
  putStatus,
  putApproveProductApi,
  putRejectProductApi,
} from "src/api/product";
import ProductForm from "src/modules/product/components/ProductForm.vue";
import { findIndex } from "src/assets/scripts/utilities/helpers";
import TimelineView from "src/modules/user/components/TimelineView.vue";

const getInitialState = () => {
  return {
    products: [],
    product: {},
    counts: {},
  };
};

export const useProductStore = defineStore("product", () => {
  const formState = ref({
    edit: false,
    open: false,
  });

  const { state: timelineFormState, toggle: toggleProductTimelineDrawer } =
    useDrawer({
      component: TimelineView,
    });

  let state = reactive(getInitialState());

  const toggleProductForm = (state = {}) => {
    state = { edit: false, open: !formState.value.open, ...state };

    const { toggleRightDrawer } = useAppStore();

    toggleRightDrawer(state.open, ProductForm);

    formState.value = state;
  };

  const setProduct = (updatedProduct) => {
    if (updatedProduct) {
      state.products[findIndex(state.products, updatedProduct, "_id")] =
        updatedProduct;

      state.product = updatedProduct;
    }
  };

  const filters = ref({
    status_cd: "approved",
  });

  const { filter, pagination, params } = usePagination(filters);

  const getProductCounts = async (options) => {
    try {
      const response = await count(options);

      state.counts = response.data?.data;

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  const getProduct = async (id) => {
    try {
      state.product = state.products?.find((product) => product._id === id);

      const response = await get(id);

      setProduct(response.data.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getProducts = async (options = {}) => {
    try {
      const [, response] = await Promise.all([
        getProductCounts({ params: params.value, ...unref(options) }),
        index({ params: params.value, ...unref(options) }),
      ]);

      state.products = response.data?.data?.data || [];

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const postProduct = async (product) => {
    try {
      const response = await post(product);

      await getProducts();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putProduct = async (product) => {
    try {
      const response = await put(product);

      await getProduct(product._id);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putProductStatus = async (product) => {
    const { _id, is_active } = product;

    await putStatus(_id, !is_active);

    await getProduct(_id);
  };

  const putApproveProduct = async (product) => {
    const response = await putApproveProductApi(product);

    await getProduct(product?._id);

    return response;
  };

  const putRejectProduct = async (product) => {
    const response = await putRejectProductApi(product);

    await getProduct(product?._id);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    timelineFormState,
    toggleProductForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),
    getProductCounts,

    getProduct,
    getProducts,
    postProduct,
    putProduct,
    putProductStatus,
    putApproveProduct,
    putRejectProduct,

    toggleProductTimelineDrawer,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProductStore, import.meta.hot));
}
